import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as fileSaver from 'file-saver';
import Button from '../../Button';
import styles from './pdf-styles.module.scss';
import { ReactComponent as RotateIcon } from './imgs/ic-rotate-left.svg';
import { ReactComponent as ZoomIn } from './imgs/ic-add.svg';
import { ReactComponent as ZoomOut } from './imgs/ic-remove.svg';
import { ReactComponent as MakeWorseIcon } from './imgs/ic-undo.svg';
import * as uniqid from 'uniqid';
import { ReactComponent as Info } from './imgs/ic-info-outline.svg';
import ReactTooltip from 'react-tooltip';
import { getFileSize } from '../../../utils/format';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { setFileSize } from './FileUploadService';
import { FileUploadModalContext } from './FileUploadModalContext';

const maxQualityLevel = 3;
const InfoTooltip = () => {
  const infoRef = useRef();
  const { selectedDocument } = useContext(FileUploadModalContext);
  const { origFileSize } = selectedDocument;
  const compressedFileSize = get(selectedDocument, 'fileBlob.size');
  const id = uniqid();
  return (
    <>
      <Info data-for={id} data-tip ref={infoRef} />
      <ReactTooltip id={id} type="dark" effect="float" className={styles.tooltipContent}>
        <TipContent
          fileCompressedSize={getFileSize(compressedFileSize).label}
          fileOriginalSize={getFileSize(origFileSize).label}
        />
      </ReactTooltip>
    </>
  );
};

export function TipContent({ fileOriginalSize, fileCompressedSize }) {
  return (
    <div className={styles.tipContent}>
      <div className={`Caption white p-3`}>
        <div className=" d-flex justify-content-between text-nowrap ">
          <div>Оригинальный размер файла</div>
          <div>{fileOriginalSize}</div>
        </div>
        <div className="d-flex justify-content-between text-nowrap ">
          <div>Размер файла после сжатия</div>
          <div>{fileCompressedSize}</div>
        </div>
      </div>
      <hr />
      <div className={`Caption disabled p-3`}>
        Данное изображение было сжато с целью предотвратить отправку файлов с большим размером.{' '}
        <br /> Вы можете повысить качество нажав на кнопку "Улучшить".
      </div>
    </div>
  );
}

function Controls({ canRotate, scale, setScale }) {
  const { setLoading, files, replaceFiles, getDocument, selectedDocument } = useContext(
    FileUploadModalContext,
  );

  const qualityLevel = selectedDocument.qualityLevel;

  const zoomIn = useCallback(() => {
    setScale(scale + 0.25);
  }, [setScale, scale]);

  const zoomOut = useCallback(() => {
    setScale(scale - 0.25);
  }, [scale, setScale]);

  const rotatePdf = useCallback(async () => {
    setLoading(true);
    const doc = await getDocument({ documentId: selectedDocument.documentId, rotate: true });

    const docIndex = files.findIndex((file) => file.documentId === selectedDocument.documentId);
    files[docIndex] = { ...files[docIndex], fileBlob: doc };
    replaceFiles([...files]);
    setLoading(false);
  }, [files, selectedDocument]);

  const makeWorse = useCallback(async () => {
    setLoading(true);
    const nQuality = selectedDocument.qualityLevel + 1;
    const doc = await getDocument({ documentId: selectedDocument.documentId, quality: nQuality });
    const docIndex = files.findIndex((file) => file.documentId === selectedDocument.documentId);
    files[docIndex] = setFileSize({ ...files[docIndex], fileBlob: doc, qualityLevel: nQuality });
    replaceFiles([...files]);
    setLoading(false);
  }, [files, selectedDocument]);

  const makeBetter = useCallback(async () => {
    const nQuality = selectedDocument.qualityLevel - 1;
    setLoading(true);
    const doc = await getDocument({ documentId: selectedDocument.documentId, quality: nQuality });
    const docIndex = files.findIndex((file) => file.documentId === selectedDocument.documentId);
    files[docIndex] = setFileSize({ ...files[docIndex], fileBlob: doc, qualityLevel: nQuality });
    replaceFiles([...files]);
    setLoading(false);
  }, [files, selectedDocument]);

  return (
    <div className={styles.controls}>
      {canRotate && (
        <div className={styles.buttonGroup}>
          <RotateIcon className="cursor-pointer" onClick={rotatePdf}>
            Rotate
          </RotateIcon>
        </div>
      )}

      {canRotate && (
        <div className={styles.buttonGroup}>
          <ZoomIn className="cursor-pointer" onClick={zoomIn}>
            +
          </ZoomIn>
          <ZoomOut className="cursor-pointer" onClick={zoomOut}>
            -
          </ZoomOut>
        </div>
      )}
      {0 !== qualityLevel && (
        <div className={styles.buttonGroup}>
          <div className="S2 cursor-pointer" onClick={makeBetter}>
            УЛУЧШИТЬ
          </div>
          <InfoTooltip />
        </div>
      )}

      {maxQualityLevel !== qualityLevel && (
        <div className={styles.buttonGroup}>
          <MakeWorseIcon className="cursor-pointer" onClick={makeWorse} />
        </div>
      )}
    </div>
  );
}

export function renderPage(page, scale, container, canvasClassName = '', imageContainer) {
  if (container) {
    const viewport = page.getViewport({ scale });
    const div = document.createElement('div');

    // Set id attribute with page-#{pdf_page_number} format
    div.setAttribute('id', 'page-' + page.pageNumber);
    div.className = imageContainer;

    // This will keep positions of child elements as per our needs
    // div.setAttribute('style', 'position: relative; margin: 5px;box-shadow: 5px 5px 10px rgba(0,0,0,0.2)');

    // Append div within div#container
    container.appendChild(div);

    // Create a new Canvas element
    let canvas = document.createElement('canvas');

    canvas.className = canvasClassName;
    // Append Canvas within div#page-#{pdf_page_number}
    div.appendChild(canvas);

    const context = canvas.getContext('2d');

    canvas.height = viewport.height;
    canvas.width = viewport.width;

    const renderContext = {
      canvasContext: context,
      viewport: viewport,
    };

    // Render PDF page
    page.render(renderContext);
  }
}

function renderPages(pdf, container, scale) {
  container.innerHTML = '';
  for (let i = 1; i <= pdf.numPages; i++) {
    // Get desired page
    pdf.getPage(i).then((page) => renderPage(page, scale, container));
  }
}

export function PdfPreview({ scale }) {
  const { selectedDocument, selectedDocumentId } = useContext(FileUploadModalContext);
  const pdfContainerRef = useRef();
  useEffect(() => {
    (async () => {
      if (selectedDocumentId) {
        const PDFJS = await import('pdfjs-dist');
        const pdfjsWorker = await import('pdfjs-dist/build/pdf.worker.entry');
        await import('pdfjs-dist/web/pdf_viewer.css');
        PDFJS.GlobalWorkerOptions.workerSrc = pdfjsWorker;
        const url = window.URL.createObjectURL(selectedDocument.fileBlob);
        const res = await PDFJS.getDocument(url).promise;
        renderPages(res, pdfContainerRef.current, scale);
      }
    })();
  }, [selectedDocument, scale]);

  return <div ref={pdfContainerRef} className={styles.pdfPreviewWrapper} />;
}

export function ImagePreview({ scale }) {
  const { selectedDocument } = useContext(FileUploadModalContext);
  const [imageUrl, setImageUrl] = useState('');
  const [width, setWidth] = useState(100);

  useEffect(() => {
    (async () => {
      setWidth(100 * scale);
    })();
  }, [scale]);

  useEffect(() => {
    (async () => {
      if (selectedDocument) {
        setImageUrl(window.URL.createObjectURL(selectedDocument.fileBlob));
      }
    })();
  }, [selectedDocument]);

  return (
    <div
      className={styles.imagePreviewContainer}
      style={{
        maxWidth: `${width}%`,
        minWidth: `${width}%`,
      }}
    >
      <img style={{ width: `100%` }} src={imageUrl} />
    </div>
  );
}

export function LinkPreview() {
  const { selectedDocument } = useContext(FileUploadModalContext);

  const saveFile = useCallback(() => {
    fileSaver.saveAs(selectedDocument.fileBlob, selectedDocument.fileName, true);
  }, [selectedDocument]);

  return (
    <div className="d-flex justify-content-center align-items-center w-100">
      <div className="text-center">
        <span>
          Предпросмотр недоступен.
          <br /> Для проверки качества сжатия&nbsp;
        </span>
        <Button isLinkStyled={true} onClick={saveFile} hasWrapper={false}>
          скачайте обработанный файл.
        </Button>
      </div>
    </div>
  );
}

export function PreviewComponent() {
  const { selectedDocument, files, replaceFiles } = useContext(FileUploadModalContext);
  const scale = selectedDocument.scale;
  const setScale = useCallback(
    (scale) => {
      const docIndex = files.findIndex((file) => file.documentId === selectedDocument.documentId);
      files[docIndex] = { ...files[docIndex], scale };
      replaceFiles([...files]);
    },
    [files, selectedDocument],
  );

  const documentType = get(selectedDocument.fileBlob, 'type', '');
  const isImage = documentType.match(/.*?(pdf|jpg|jpeg|png|bmp|gif|tif|tiff)$/);
  const allowControls = documentType.match(/.*?(pdf|tif|tiff|jpg|jpeg|png)$/);
  const isPdf = documentType.match(/.*?(pdf)$/);
  const canRotate = !documentType.match(/.*?(tiff|tif)$/);

  return get(selectedDocument, 'documentId') ? (
    <>
      {allowControls && <Controls canRotate={canRotate} scale={scale} setScale={setScale} />}
      <div className="overflow-auto" style={{ maxHeight: '100%', width: '100%', display: 'flex' }}>
        {isPdf && <PdfPreview scale={scale} />}
        {documentType.match(/.*?(jpg|jpeg|png|bmp|gif)$/) && <ImagePreview scale={scale} />}
        {documentType.match(/.*?(tif|tiff)$/) && <LinkPreview scale={scale} />}
        {!isImage && (
          <div className="H3 w-100 text-center align-self-center">Предпросмотр недоступен</div>
        )}
      </div>
    </>
  ) : null;
}

TipContent.propTypes = {
  fileCompressedSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fileOriginalSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

Controls.propTypes = {
  canRotate: PropTypes.bool,
  scale: PropTypes.number,
  setScale: PropTypes.func,
};

PdfPreview.propTypes = {
  scale: PropTypes.number,
};

ImagePreview.propTypes = {
  scale: PropTypes.number,
};
